// import { CheckIcon } from "lucide-react";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import type {
  AppliedPromotion,
  CheckoutSummaryCardHandle,
  CheckoutSummaryCardProps,
} from "@/features/auth/subscription/components/SubscriptionType.ts";

import { Badge } from "@/components/badge";
import { Skeleton } from "@/components/skeleton";
import { env } from "@/config/env";
import { toIsoCurrency } from "@/features/auth/subscription/utils/purchaseEvent";

import {
  couponBadgeLabel,
  formatMoney,
  intervalLabels,
  // planBenefits,
  thenLine,
} from "../utils/checkoutPricing";
import { CheckoutSocialProof } from "./CheckoutSocialProof";

export const CheckoutSummaryCard = forwardRef<
  CheckoutSummaryCardHandle,
  CheckoutSummaryCardProps
>(function CheckoutSummaryCard(
  {
    initialCoupon,
    interval,
    onApplyingChange,
    onPromoApplied,
    plan,
    showPromo,
  },
  ref,
) {
  const [promoCode, setPromoCode] = useState("");
  const [isApplying, setIsApplying] = useState(
    () => !!(showPromo && initialCoupon?.trim()),
  );
  const [applied, setApplied] = useState<AppliedPromotion | null>(null);
  const hasAutoAppliedRef = useRef(false);

  const currencyCode = toIsoCurrency(plan.currency, "USD");
  const fullMajor = Number(plan.price);
  const dueTodayMajor = applied ? applied.finalAmountCents / 100 : fullMajor;
  const labels = intervalLabels(interval);
  const badge = applied ? couponBadgeLabel(applied.coupon, currencyCode) : null;

  const applyCode = async (code: string): Promise<boolean> => {
    const couponCode = code.trim();

    if (!couponCode || applied) return !!applied;
    setIsApplying(true);
    onApplyingChange?.(true);
    try {
      const base = env.VITE_PHP_URL.replace(/\/$/, "");

      //TODO transfer this service to Node
      const res = await fetch(`${base}/api/stripe/promotion-code/apply`, {
        body: JSON.stringify({
          amount: Math.round(fullMajor * 100),
          code: couponCode,
          currency: currencyCode.toLowerCase(),
        }),
        headers: { "Content-Type": "application/json" },
        method: "POST",
      });
      const data = await res.json().catch(() => null);

      if (!res.ok) throw new Error(data?.message || "Invalid code");

      const promotion: AppliedPromotion = {
        code: data.code || couponCode,
        coupon: {
          amountOff: data.coupon?.amount_off ?? null,
          currency: data.coupon?.currency ?? null,
          duration: data.coupon?.duration ?? "once",
          durationInMonths: data.coupon?.duration_in_months ?? null,
          percentOff: data.coupon?.percent_off ?? null,
        },
        discountAmountCents: data.discount_amount,
        finalAmountCents: data.final_amount,
        originalAmountCents: data.original_amount,
        promotionCodeId: data.promotion_code_id ?? null,
      };

      setApplied(promotion);
      setPromoCode(promotion.code);
      onPromoApplied(promotion);

      return true;
    } catch {
      return false;
    } finally {
      setIsApplying(false);
      onApplyingChange?.(false);
    }
  };

  useEffect(() => {
    if (!showPromo || !initialCoupon || hasAutoAppliedRef.current) return;
    hasAutoAppliedRef.current = true;
    setPromoCode(initialCoupon);
    void applyCode(initialCoupon).then((isApplied) => {
      if (!isApplied) setPromoCode("");
    });
  }, [showPromo, initialCoupon]);

  useImperativeHandle(ref, () => ({
    applyPendingPromo: async () => {
      if (!showPromo || !promoCode.trim() || applied) return true;

      return applyCode(promoCode);
    },
  }));

  return (
    <div className="flex flex-col gap-5 rounded-[20px] border border-border-secondary bg-bg-primary p-8 shadow-sm">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-bold text-text-primary">{plan.name}</h2>
          <div className="flex items-baseline gap-1">
            {isApplying ? (
              <Skeleton className="h-11 w-40 rounded-md" />
            ) : (
              <span className="text-display-md font-bold text-text-primary">
                {formatMoney(dueTodayMajor, currencyCode)}
              </span>
            )}
            <span className="text-sm font-medium text-text-secondary">
              / {labels.unit}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-xs text-text-tertiary">
          <span>{labels.renews}</span>
          <span
            aria-hidden
            className="size-[3px] rounded-full bg-fg-tertiary"
          />
          <span>Cancel anytime</span>
        </div>
      </div>

      {plan.description && (
        <div className="flex flex-col gap-3 border-t border-border-secondary pt-5">
          <p className="text-xs font-medium text-text-secondary">
            {plan.description}
          </p>
          {/* Benefit bullets hidden by product decision, only the Stripe description shows for now.
          <ul className="flex flex-col gap-2">
            {planBenefits(plan.name).map((benefit) => (
              <li
                className="flex items-center gap-2 text-sm text-text-secondary"
                key={benefit}
              >
                <CheckIcon
                  aria-hidden
                  className="size-4 text-text-primary"
                  strokeWidth={3}
                />
                {benefit}
              </li>
            ))}
          </ul>
          */}
        </div>
      )}

      <div className="flex flex-col gap-1 border-t border-border-secondary pt-5">
        <div className="flex items-center justify-between">
          <span className="text-md font-bold text-text-primary">
            Total due today
          </span>
          {isApplying ? (
            <Skeleton className="h-7 w-24 rounded-md" />
          ) : (
            <span className="text-xl font-bold text-text-primary">
              {formatMoney(dueTodayMajor, currencyCode)}
            </span>
          )}
        </div>
        {badge && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-text-tertiary">Applied coupon</span>
            <Badge isRounded size="small" variant="success">
              {badge}
            </Badge>
          </div>
        )}
        {isApplying ? (
          <Skeleton className="h-5 w-56 rounded-md" />
        ) : (
          <p className="text-xs text-text-tertiary">
            {thenLine({
              coupon: applied?.coupon ?? null,
              currencyCode,
              discountedMajor: applied ? applied.finalAmountCents / 100 : null,
              fullMajor,
              interval,
              today: new Date(),
            })}
          </p>
        )}
      </div>

      <CheckoutSocialProof />
    </div>
  );
});
